import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    GeographyCreatePayload,
    GeographyCreateConflict,
    GeographyRestoreDeletedPayload,
    GeographyUpdatePayload,
    IGeographyDetail,
} from "@/customTypings/reference/Geography";
import { GeographyCreateConflictError } from "@/customTypings/reference/Geography";
import { authHeader, handleResponse } from "../../utils";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";

export const geographiesService = {
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`/api/referencedata/geographies/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<IGeographyDetail>;
}

async function create(payload: GeographyCreatePayload): Promise<IGeographyDetail> {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    const response = await fetch(`/api/referencedata/geographies/create`, requestOptions);
    const text = await response.text();
    let data: unknown = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        /* non-JSON body */
    }

    if (response.status === 409 && data && typeof data === "object" && "code" in data) {
        throw new GeographyCreateConflictError(data as GeographyCreateConflict);
    }

    return handleResponse({
        text: async () => text,
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
    }) as Promise<IGeographyDetail>;
}

function restoreDeletedWithCreateValues(payload: GeographyRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/geographies/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<IGeographyDetail>;
}

function update(payload: GeographyUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/geographies/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<IGeographyDetail>>;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/referencedata/geographies/${action}?id=${encodeURIComponent(id)}`,
        requestOptions
    ).then(handleResponse);
}
